import { MapPin, UserCheck, Briefcase, CalendarDays, Clock, Camera, ChevronRight, Mail } from "lucide-react";
import { DepotTag } from "@/components/DepotPicker";
import { fmtDate, fmtDuration, fmtTime, jobTypeLabel, todayLocal } from "@/lib/format";

const today = todayLocal;

const jobStatus = (j) => {
  if (j.status === "completed") return ["Completed", "bg-stone-500/15 text-stone-500"];
  if (j.scheduled_date && j.scheduled_date > today()) return [`Scheduled ${fmtDate(j.scheduled_date)}`, "bg-amber-500/15 text-amber-600"];
  if (j.open_entry_id) return ["On site", "bg-emerald-500/15 text-emerald-600"];
  if ((j.total_seconds || 0) > 0) return ["In progress", "bg-blue-500/15 text-blue-600"];
  return ["Not started", "bg-secondary text-muted-foreground"];
};

const Row = ({ onClick, testId, children }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={testId}
    className="w-full text-left flex items-center gap-4 px-4 sm:px-5 py-3.5 border-t border-border first:border-t-0 hover:bg-secondary/60 disabled:hover:bg-transparent transition-colors">
    <div className="min-w-0 flex-1">{children}</div>
    {onClick && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
  </button>
);

const Meta = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 min-w-0"><Icon className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{children}</span></span>
);

export const JobRow = ({ job: j, onOpen }) => {
  const [label, cls] = jobStatus(j);
  return (
    <Row onClick={() => onOpen(j.id)} testId={`overview-job-${j.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold truncate">{j.name}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>
        <DepotTag id={j.depot_id} />
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <Meta icon={Briefcase}>{[jobTypeLabel(j), j.client].filter(Boolean).join(" · ") || "No client"}</Meta>
        <Meta icon={UserCheck}>{j.assigned_name || j.assigned_email || "Unassigned"}{j.team_count ? ` +${j.team_count}` : ""}</Meta>
        {j.status === "completed"
          ? <><Meta icon={CalendarDays}>{fmtDate(j.completed_at)}</Meta><Meta icon={Clock}>{fmtDuration(j.total_seconds)}</Meta><Meta icon={Camera}>{j.photo_count || 0}</Meta></>
          : j.address && <Meta icon={MapPin}>{j.address}</Meta>}
      </div>
    </Row>
  );
};

export const PendingRow = ({ job: j, onOpen }) => (
  <Row onClick={() => onOpen(j.id)} testId={`overview-pending-${j.id}`}>
    <span className="font-semibold">{j.name}</span>
    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <Meta icon={Mail}>Waiting for {j.assigned_email || "a crew member"} to sign in</Meta>
      {j.client && <Meta icon={Briefcase}>{j.client}</Meta>}
    </div>
  </Row>
);

export const OnSiteRow = ({ p, now, onOpen }) => (
  <Row onClick={() => onOpen(p.job_id)} testId={`overview-onsite-${p.user_id}-${p.job_id}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
      <span className="font-semibold">{p.name}</span>
      <span className="text-muted-foreground text-sm">at</span>
      <span className="font-semibold truncate">{p.job_name}</span>
      <DepotTag id={p.depot_id} />
    </div>
    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <Meta icon={Clock}>Since {fmtTime(p.since)} · {fmtDuration((now - new Date(p.since).getTime()) / 1000)}</Meta>
      {p.client && <Meta icon={Briefcase}>{p.client}</Meta>}
      {p.address && <Meta icon={MapPin}>{p.address}</Meta>}
    </div>
  </Row>
);

export const WorkerRow = ({ w, here, onOpen }) => (
  <Row onClick={here ? () => onOpen(here.job_id) : undefined} testId={`overview-worker-${w.email}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-semibold">{w.name || w.email}</span>
      {here
        ? <span className="rounded-full px-2 py-0.5 text-[11px] font-medium bg-emerald-500/15 text-emerald-600">On site · {here.job_name}</span>
        : <span className="rounded-full px-2 py-0.5 text-[11px] font-medium bg-secondary text-muted-foreground">Not on site</span>}
      <DepotTag id={w.home_depot_id} />
    </div>
    <p className="mt-1 text-xs text-muted-foreground truncate">{w.email}{w.employment_type ? ` · ${w.employment_type.replace("_", " ")}` : ""}</p>
  </Row>
);
